import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useSearchParams } from 'react-router-dom';
import { Grid, Paper, Tab, Tabs, Stack, ShipsIcon } from '@esgian/esgianui';
import HistoricalActivity from '@components/Sections/ShipAnalytics/RegionSection/HistoricalActivity/HistoricalActivity';
import moment from 'moment';
import { YtdPortCallStatsTable } from '@components/Tables';
import {
  RegionActivitySection,
  RegionOverviewSection,
  RegionPortStatisticsSection
} from '@components/Sections/ShipAnalytics/RegionSection/SubSections';
import PortWaitingTimeSection from '@components/Sections/ShipAnalytics/PortProfileSection/SubSections/PortWaitingTimeSection';
import { useDispatch, useSelector } from 'react-redux';

import { cleanUpCurrentParams, formatNumberReadable } from '@helpers';
import {
  getRegionProfileSection,
  setRegionProfileSection
} from '@store/features/filters/RegionProfilePage/RegionProfileReducer';
import { GEOGRAPHICAL_TYPES } from '@constants';
import { getCommercialCategoryType, getOperatorType, setGeographicalType } from '@store/features';
import KpiBox from '@components/Display/KpiBox';
import { TextWithTooltipIcon } from '@components';
import { REGION_ACTIVITY_TITLE, REGION_HISTORICAL_TITLE } from '@constants/tooltips';
import { useQuery } from '@tanstack/react-query';
import { getRegionPortStats } from '@api';
import { useSegment } from '@hooks';

function RegionSection({
  loading,
  regionId,
  regionName,
  regionTopPortsData,
  handleSelectYear,
  selectedYear,
  portCongestionData
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const operatorType = useSelector(getOperatorType);
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const params = useParams();
  const { segment } = useSegment();
  const dispatch = useDispatch();
  const section = useSelector(getRegionProfileSection);

  const regionPortStatsQuery = useQuery({
    queryKey: [
      'regionPortStats',
      {
        geoIds: [params.regionId],
        geoType: GEOGRAPHICAL_TYPES.COUNTRY,
        SegmentTypeId: segment.id,
        operatorTypeId: operatorType,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!params.regionId,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getRegionPortStats(queryKey[1], signal)
        .then((result) => result)
        .catch(() => null)
  });

  const handleSectionChange = (_, val) => {
    const params = cleanUpCurrentParams(searchParams);
    dispatch(setRegionProfileSection(val));
    params.set('section', val);
    setSearchParams(params);
  };

  useEffect(() => {
    dispatch(setGeographicalType(GEOGRAPHICAL_TYPES.REGION));
    if (searchParams.has('section')) {
      dispatch(setRegionProfileSection(parseInt(searchParams.get('section'))));
    } else {
      searchParams.set('section', section);
      setSearchParams(searchParams);
    }
  }, []);

  const isMapFullscreen = searchParams.get('overviewMapFullscreen') === 'true';

  // If map fullscreen is active, just return VesselOverviewSection (which handles full screen)
  if (isMapFullscreen) {
    return <RegionActivitySection regionId={regionId} />;
  }
  const { prevYearNumPortCalls, prevYearYtdNumPortCalls, ytdNumPortCalls } =
    regionPortStatsQuery.data || {};

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          spacing={1} // Applies spacing between items on the same row
          useFlexGap
          sx={{ flexWrap: 'wrap' }}>
          <KpiBox
            loading={regionPortStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={formatNumberReadable(ytdNumPortCalls) ?? ''}
            title={'Port Calls (YTD)'}
          />
          <KpiBox
            loading={regionPortStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={formatNumberReadable(prevYearYtdNumPortCalls) ?? ''}
            title={`${moment().subtract(12, 'months').format('YYYY')} Port Calls YTD`}
          />
          <KpiBox
            loading={regionPortStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={formatNumberReadable(prevYearNumPortCalls) ?? ''}
            title={`${moment().subtract(12, 'months').format('YYYY')} # Port Calls`}
          />
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Grid container>
            <Grid item xs={12}>
              <Tabs
                value={section}
                onChange={handleSectionChange}
                variant="scrollable"
                aria-label="scrollable prevent tabs example">
                <Tab value={1} label="Overview" />
                <Tab value={2} label="Port Statistics" />
                <Tab
                  value={3}
                  label={
                    <TextWithTooltipIcon
                      color={'inherit'}
                      labelVariant={'inherit'}
                      labelBold={false}
                      label={'Region Activity'}
                      tooltipText={REGION_ACTIVITY_TITLE}
                    />
                  }
                />
                <Tab
                  value={4}
                  label={
                    <TextWithTooltipIcon
                      color={'inherit'}
                      labelVariant={'inherit'}
                      labelBold={false}
                      label={'Historical Activity'}
                      tooltipText={REGION_HISTORICAL_TITLE}
                    />
                  }
                />
                <Tab value={5} label="Top Ports in Region" />
                <Tab value={6} label="Port Waiting Time" />
              </Tabs>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {section === 1 && <RegionOverviewSection regionName={regionName} geoId={regionId} />}
          {section === 2 && (
            <RegionPortStatisticsSection
              loading={regionPortStatsQuery.isFetching}
              regionName={regionName}
              regionPortStatsData={regionPortStatsQuery.data}
              regionPortsData={regionTopPortsData}
            />
          )}
          {section === 3 && <RegionActivitySection regionId={regionId} />}
          {section === 4 && <HistoricalActivity regionId={regionId} regionName={regionName} />}
          {section === 5 && (
            <Grid item xs={12}>
              <YtdPortCallStatsTable
                topPortsData={regionTopPortsData}
                handleSelectYear={handleSelectYear}
                selectedYear={selectedYear}
                subTableHeader={`Operators YTD ${selectedYear}`}
              />
            </Grid>
          )}
          {section === 6 && (
            <Grid item xs={12}>
              <PortWaitingTimeSection
                assetLevel={'port'}
                loading={loading}
                name={regionName}
                portCongestionData={portCongestionData}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

RegionSection.propTypes = {
  regionId: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  portCongestionData: PropTypes.object,
  regionName: PropTypes.string,
  regionTopPortsData: PropTypes.arrayOf(PropTypes.object),
  handleSelectYear: PropTypes.func.isRequired,
  selectedYear: PropTypes.number
};

RegionSection.defaultProps = {
  regionTopPortsData: [],
  portCongestionData: {},
  regionName: '',
  loading: false,
  selectedYear: moment().year()
};

export default RegionSection;
